import { safeJsonParse } from '@/utils/safeJsonParse.ts';

import { logger } from '../logger';

/**
 * Error thrown for non-2xx HTTP responses. Carries the structured HTTP
 * `status` so callers can branch on it (e.g. 413 -> "file too large") instead
 * of pattern-matching the message string.
 */
export class RestApiError extends Error {
	readonly status: number;
	readonly responseText: string;

	constructor(status: number, responseText: string) {
		super(`Error ${status}: ${responseText}`);
		this.name = 'RestApiError';
		this.status = status;
		this.responseText = responseText;
	}
}

export class RestApi {
	protected baseUrl: string;

	constructor(baseUrl: string) {
		this.baseUrl = baseUrl;
	}

	// `expectedErrorStatuses` names the non-2xx statuses the caller handles as a
	// normal outcome. Those skip the generic error logging below, so a handled
	// status does not reach Splunk at error level and count against error alerts.
	// The rejection is unchanged, so every caller still branches on `status`.
	public request = <ExpectedResponseType>(
		method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
		endpoint: string,
		body?: object,
		additionalHeaders?: Record<string, string>,
		expectedErrorStatuses?: readonly number[],
	): Promise<ExpectedResponseType> => {
		return new Promise((resolve, reject) => {
			fetch(`${this.baseUrl}${endpoint}`, {
				method,
				headers: {
					'Content-Type': 'application/json',
					...additionalHeaders,
				},
				mode: 'cors',
				body: body ? JSON.stringify(body) : undefined,
			})
				.then(async (response) => {
					if (!response.ok) {
						const isExpected =
							expectedErrorStatuses?.includes(response.status) ?? false;
						if (!isExpected) {
							logger.error('REST response', response);
						}
						let responseText = await response.text();
						const [parsed, isJson] = safeJsonParse(responseText);
						if (
							isJson &&
							typeof parsed === 'object' &&
							parsed !== null &&
							'detail' in parsed &&
							typeof parsed.detail === 'string'
						) {
							responseText = parsed.detail;
						}
						if (!isExpected) {
							logger.error('REST error', { responseText, parsed, isJson });
						}
						return reject(new RestApiError(response.status, responseText));
					}

					if (
						response.status === 204 ||
						response.headers.get('content-length') === '0'
					) {
						return undefined as ExpectedResponseType;
					}

					return response.json();
				})
				.then(resolve)
				.catch(reject);
		});
	};

	public get = <ExpectedResponseType>(
		endpoint: string,
		additionalHeaders?: Record<string, string>,
	): Promise<ExpectedResponseType> => {
		return this.request<ExpectedResponseType>(
			'GET',
			endpoint,
			undefined,
			additionalHeaders,
		);
	};

	public post = <ExpectedResponseType>(
		endpoint: string,
		body: object,
		additionalHeaders?: Record<string, string>,
	): Promise<ExpectedResponseType> => {
		return this.request<ExpectedResponseType>(
			'POST',
			endpoint,
			body,
			additionalHeaders,
		);
	};

	public put = <ExpectedResponseType>(
		endpoint: string,
		body: object,
		additionalHeaders?: Record<string, string>,
	): Promise<ExpectedResponseType> => {
		return this.request<ExpectedResponseType>(
			'PUT',
			endpoint,
			body,
			additionalHeaders,
		);
	};

	public delete = <ExpectedResponseType>(
		endpoint: string,
		additionalHeaders?: Record<string, string>,
	): Promise<ExpectedResponseType> => {
		return this.request<ExpectedResponseType>(
			'DELETE',
			endpoint,
			undefined,
			additionalHeaders,
		);
	};
}
